/**
 * Share policy frozen contracts (sharing redesign · Phase 5).
 *
 * FROZEN. Producers and consumers below are the only owners of each concern;
 * an implementation module may sit beneath these types but must not restate
 * them, widen them, or introduce a second policy definition.
 *
 * Producers
 * - studio intent: the Phase 5 draft builder inspector (renderer);
 * - normalized truth: `normalizeSharePolicy` (one implementation, Agent 1);
 * - persistence: `client_links.policy_json` (local db v58, main process);
 * - transport: `clientLinks.*` IPC, then the worker `links` row;
 * - enforcement: the worker share routes;
 * - client projection: `SharePolicyClientDto`.
 *
 * Consumers
 * - desktop summary strip + enabled controls (renderer);
 * - upload output plan (`plannedOutputsFor`, Agent 3);
 * - worker route gates;
 * - client-web gallery (Phase 6).
 *
 * Every one of those derives from the SAME normalized policy. A surface that
 * recomputes a permission from raw booleans is a contract violation.
 */

import type { VariantFailureCause } from '../../electron/clientLinks/failureCauses'

export const SHARE_POLICY_CONTRACT_VERSION = 1 as const

/** The only policy version this build authors. Older/newer are read-only. */
export const SHARE_POLICY_VERSION = 2 as const

// ─── Access ─────────────────────────────────────────────────────────────────

export type ShareAccessMode = 'anyone' | 'invited'

/**
 * Passcode is CONTEXTUAL to `anyone`. Invited access uses its own identity
 * gate (a code to an invited address, worker migration 032), so the union
 * makes a hidden-but-stored passcode structurally unrepresentable rather than
 * leaving a stale secret active behind an identity gate.
 */
export type ShareAccessV1 = { mode: 'anyone'; passcode: string | null } | { mode: 'invited' }

// ─── What a client may do ───────────────────────────────────────────────────

/**
 * ONE exclusive value, not a bag of booleans. These are exactly the three
 * states the worker can distinguish today:
 *
 *   select  · POST /:token/picks   requires picks_enabled === 1
 *   approve · POST /:token/approve requires picks_enabled === 0 AND a
 *             download surface (workers/src/routes/share.ts)
 *   view    · neither route accepts the visitor
 */
export type ShareClientMode = 'view' | 'select' | 'approve'

export type ShareClientAbility = 'view' | 'select' | 'comment' | 'approveDelivery'

/**
 * Documented permission matrix. `comment` is TRUE in every mode because
 * POST /:token/comments is not gated by the client mode on the worker today.
 * It is therefore a documented capability, never a studio-facing toggle — the
 * builder must not offer a control the worker cannot enforce.
 */
export const SHARE_CLIENT_MODE_MATRIX: Readonly<
  Record<ShareClientMode, Readonly<Record<ShareClientAbility, boolean>>>
> = Object.freeze({
  view: Object.freeze({ view: true, select: false, comment: true, approveDelivery: false }),
  select: Object.freeze({ view: true, select: true, comment: true, approveDelivery: false }),
  approve: Object.freeze({ view: true, select: false, comment: true, approveDelivery: true }),
})

export function clientModeAllows(mode: ShareClientMode, ability: ShareClientAbility): boolean {
  return SHARE_CLIENT_MODE_MATRIX[mode][ability]
}

// ─── Downloads ──────────────────────────────────────────────────────────────

/**
 * Prepared copies the studio had ShotSelect BUILD. Never camera originals.
 *
 *   web     · the existing 3000 px WebP gallery object (`zoom`)
 *   largest · a high-quality re-encode capped at 6000 px, ALWAYS re-encoded
 *   both    · web and largest are both offered
 *   none    · no prepared download
 */
export type PreparedFormat = 'none' | 'web' | 'largest' | 'both'

/** Prepared copies a client may be offered, in ascending size order. */
export type PreparedCopy = 'web' | 'largest'

export function preparedCopiesFor(format: PreparedFormat): readonly PreparedCopy[] {
  switch (format) {
    case 'web':
      return ['web']
    case 'largest':
      return ['largest']
    case 'both':
      return ['web', 'largest']
    default:
      return []
  }
}

export interface ShareDownloadsV1 {
  /** Master switch for PREPARED copies only. Never implies originals. */
  allowDownloads: boolean
  preparedFormat: PreparedFormat
}

// ─── Camera originals ───────────────────────────────────────────────────────

/**
 * WHICH camera files the client may download. "Original" means the file the
 * camera wrote, handed over byte-for-byte — not a copy ShotSelect made.
 *
 *   jpg  · the camera's own rendered file (the JPEG off the card)
 *   raw  · the camera's own RAW file (.ARW, .NEF, .CR3, …), verbatim
 *   both · JPEG and RAW side by side, the RAW+JPEG delivery photographers
 *          actually ask for
 *
 * A photo only yields the halves it HAS: a RAW-only frame has no camera JPEG,
 * and a JPEG-only frame has no RAW. The plan emits what exists and the builder
 * says so, rather than promising a file the card never held.
 */
export type OriginalsKind = 'jpg' | 'raw' | 'both'

/** Camera files a client may be offered, JPEG first. */
export type OriginalCopy = 'jpg' | 'raw'

export function originalCopiesFor(kind: OriginalsKind): readonly OriginalCopy[] {
  switch (kind) {
    case 'jpg':
      return ['jpg']
    case 'raw':
      return ['raw']
    case 'both':
      return ['jpg', 'raw']
    default:
      return []
  }
}

export interface ShareOriginalsV1 {
  /**
   * Camera originals. A SEPARATE explicit opt-in, never inferred from
   * `preparedFormat`. When false the uploader must not read, stage, prepare,
   * upload, or reference camera JPEG or RAW source bytes for delivery.
   */
  allowOriginals: boolean
  kind: OriginalsKind
}

// ─── Membership ─────────────────────────────────────────────────────────────

export type ShareMembershipMode = 'snapshot' | 'sync'

/**
 * A sync source must be DURABLE and re-resolvable. An ad-hoc search or filter
 * result is not a source: it cannot be resolved again later, so it can never
 * become a silent live query over a client's gallery.
 */
export type SyncSourceKind = 'event' | 'folder' | 'collection'

export interface SyncSourceIdentity {
  kind: SyncSourceKind
  /** Stable resolver key (event id, absolute folder key, collection id). */
  id: string
  /** Studio-facing label. Never sent to a client. */
  label: string
}

export type ShareMembershipV1 = { mode: 'snapshot' } | { mode: 'sync'; source: SyncSourceIdentity }

// ─── The policy ─────────────────────────────────────────────────────────────

export interface SharePolicyV1 {
  version: 1
  access: ShareAccessV1
  clientMode: ShareClientMode
  downloads: ShareDownloadsV1
  originals: ShareOriginalsV1
  /** Days from publish. The worker caps at 90; 0 is not a policy. */
  expiryDays: number
  membership: ShareMembershipV1
}

/** V2 makes browse-only enforceable. V0/V1 keep their historical comment grant. */
export interface SharePolicyV2 extends Omit<SharePolicyV1, 'version'> {
  version: 2
}
export type SharePolicy = SharePolicyV1 | SharePolicyV2

export type PublicShareRole = 'viewer' | 'reviewer'

/** The sole versioned comment-capability projection. Never reinterpret old links. */
export function shareClientCapabilities(version: number, mode: ShareClientMode) {
  return version === 2 && mode === 'view'
    ? Object.freeze({ ...SHARE_CLIENT_MODE_MATRIX.view, comment: false })
    : SHARE_CLIENT_MODE_MATRIX[mode]
}

/** Download grants are independent of the contribution role. Legacy view stays explicit. */
export function publicShareRole(policy: SharePolicy): PublicShareRole | null {
  if (policy.clientMode === 'approve') return null
  if (policy.clientMode === 'select') return 'reviewer'
  return policy.version === 2 ? 'viewer' : null
}

/**
 * A record read back from disk or the worker. `version` is the discriminator;
 * an unknown (future) version must fail closed rather than being coerced into
 * V1 or reinterpreted through legacy flags.
 */
export interface UnknownVersionedPolicy {
  version: number
  [key: string]: unknown
}

export type StoredSharePolicy = SharePolicy | UnknownVersionedPolicy

export function isSharePolicyV1(value: StoredSharePolicy | null): value is SharePolicyV1 {
  return !!value && value.version === 1
}

export const SHARE_POLICY_EXPIRY_MIN_DAYS = 1 as const
export const SHARE_POLICY_EXPIRY_MAX_DAYS = 90 as const

// ─── Normalization outcome ──────────────────────────────────────────────────

/**
 * Bounded rejection codes. Normalization REJECTS impossible or dead
 * configurations rather than silently repairing intent it cannot infer.
 */
export type SharePolicyRejection =
  | 'unsupported_version'
  | 'malformed_policy'
  | 'downloads_on_without_prepared_format'
  | 'prepared_format_without_downloads'
  | 'approve_without_delivery_surface'
  | 'passcode_on_invited_access'
  | 'empty_passcode'
  | 'expiry_out_of_range'
  | 'originals_on_without_kind'
  | 'sync_source_missing'

export type SharePolicyNormalizeResult =
  { ok: true; policy: SharePolicy } | { ok: false; rejection: SharePolicyRejection }

/**
 * True when the policy grants the client SOME download surface — a permitted
 * prepared copy, or an explicitly permitted original. The client download UI
 * is shown when this is true and never merely because a format was picked.
 */
export function hasDownloadSurface(policy: SharePolicy): boolean {
  const prepared = policy.downloads.allowDownloads
    ? preparedCopiesFor(policy.downloads.preparedFormat)
    : []
  const originals = policy.originals.allowOriginals ? originalCopiesFor(policy.originals.kind) : []
  return prepared.length > 0 || originals.length > 0
}

// ─── Client-facing DTO ──────────────────────────────────────────────────────

/**
 * The ONLY policy shape that may cross to a client. It carries no passcode
 * secret, no absolute path, no idempotency key, no local job detail, no
 * membership source identity, and no recipient data.
 */
export interface SharePolicyClientDto {
  policyVersion: number
  access: ShareAccessMode
  /** Whether a passcode is required. Never the passcode. */
  passcodeRequired: boolean
  can: Readonly<Record<ShareClientAbility, boolean>>
  downloads: {
    /** Copies ShotSelect prepared, ascending in size. */
    prepared: readonly PreparedCopy[]
    /** Camera files, JPEG first. Empty when originals are not permitted. */
    originals: readonly OriginalCopy[]
  }
  expiresAt: number
}

/** Studio-facing summary. Same normalized source as the DTO and the gates. */
export interface SharePolicyDesktopSummary {
  accessLabel: string
  clientModeLabel: string
  downloadLabel: string
  originalsLabel: string
  passcodeProtected: boolean
  expiryDays: number
  membershipLabel: string
}

// ─── Prepared outputs (upload plan boundary) ────────────────────────────────

/**
 * Every distinct object the uploader can produce for one photo.
 *
 *   thumb/display · gallery-only, always produced, never a download
 *   web           · the 3000 px `zoom` object; also the web download
 *   largest       · re-encoded high-res PREPARED copy (new in Phase 5)
 *   original      · the camera's own rendered file (the JPEG off the card),
 *                   verbatim bytes. Never a re-encode.
 *   raw           · the camera's own RAW file, verbatim bytes. Never a
 *                   re-encode, and NOT limited to a RAW+JPEG pair.
 *
 * `largest` and the two camera outputs are DIFFERENT things. `largest` is
 * always a copy ShotSelect built; `original` and `raw` are always the card's
 * own bytes.
 *
 * The pre-Phase-5 behaviour this corrects: the legacy `original` object was
 * verbatim only for a rendered source. For a RAW source it was the camera's
 * embedded preview re-encoded to WebP, and the verbatim RAW shipped only when
 * the frame happened to have a JPEG sibling. So a RAW-only shoot could never
 * deliver a RAW at all, however the studio set the download tier.
 */
export type PreparedOutputKind = 'thumb' | 'display' | 'web' | 'largest' | 'original' | 'raw'

/** The two outputs that are the camera's own bytes rather than a copy. */
export const CAMERA_ORIGINAL_OUTPUTS: readonly PreparedOutputKind[] = ['original', 'raw']

export const GALLERY_OUTPUTS: readonly PreparedOutputKind[] = ['thumb', 'display', 'web']

/**
 * The exact outputs a policy authorizes. Gallery outputs are unconditional;
 * `largest` follows the prepared format; `original` and `raw` follow
 * `allowOriginals` ALONE.
 */
export function plannedOutputsFor(policy: SharePolicy): readonly PreparedOutputKind[] {
  const outputs: PreparedOutputKind[] = ['thumb', 'display', 'web']
  const prepared = policy.downloads.allowDownloads
    ? preparedCopiesFor(policy.downloads.preparedFormat)
    : []
  if (prepared.includes('largest')) outputs.push('largest')
  if (policy.originals.allowOriginals) {
    const copies = originalCopiesFor(policy.originals.kind)
    // 'jpg' is the camera's rendered file, so it maps to the `original`
    // object; 'raw' maps to `raw`. A photo that lacks one half simply yields
    // no output for it — that filtering is per-photo and belongs to the
    // upload plan, not here.
    if (copies.includes('jpg')) outputs.push('original')
    if (copies.includes('raw')) outputs.push('raw')
  }
  return outputs
}

/**
 * Hard invariant, asserted by contract tests and by the upload engine: with
 * originals off, no camera-original or RAW output is ever planned.
 */
export function originalsAreForbidden(policy: SharePolicy): boolean {
  return (
    policy.originals.allowOriginals === false ||
    originalCopiesFor(policy.originals.kind).length === 0
  )
}

// ─── Readiness ──────────────────────────────────────────────────────────────

export type ReadinessBlocker =
  | 'policy_invalid'
  | 'no_photos'
  | 'membership_stale'
  | 'policy_stale'
  | 'outputs_incomplete'
  | 'missing_sources'
  | 'job_running'
  | 'job_failed'

export interface ReadinessInput {
  /** Draft lifecycle state, from the draft store. */
  state: string
  policyValid: boolean
  membershipCount: number
  missingSourceCount: number
  /** Revisions recorded on the draft. */
  membershipRevision: number
  policyRevision: number
  /** Revisions the completed preparation work was performed against. */
  preparedMembershipRevision: number | null
  preparedPolicyRevision: number | null
  outputsCompleted: number
  outputsTotal: number
  jobActive: boolean
  jobFailed: boolean
}

export interface ReadinessResult {
  ready: boolean
  blockers: readonly ReadinessBlocker[]
}

/**
 * Pure readiness calculation. "Make link live" is enabled only when this
 * returns `ready`. Every blocker is bounded and photographer-readable.
 */
export function calculateReadiness(input: ReadinessInput): ReadinessResult {
  const blockers: ReadinessBlocker[] = []
  if (!input.policyValid) blockers.push('policy_invalid')
  if (input.membershipCount <= 0) blockers.push('no_photos')
  if (input.missingSourceCount > 0) blockers.push('missing_sources')
  if (input.jobActive) blockers.push('job_running')
  if (input.jobFailed) blockers.push('job_failed')
  if (input.preparedMembershipRevision !== input.membershipRevision) {
    blockers.push('membership_stale')
  }
  if (input.preparedPolicyRevision !== input.policyRevision) blockers.push('policy_stale')
  if (input.outputsTotal <= 0 || input.outputsCompleted < input.outputsTotal) {
    blockers.push('outputs_incomplete')
  }
  return { ready: blockers.length === 0, blockers }
}

// ─── Bounded error vocabulary ───────────────────────────────────────────────

/** Reuses the existing upload cause vocabulary; adds only a cancel outcome. */
export type DraftErrorCategory = VariantFailureCause | 'cancelled'

export const DRAFT_ERROR_CATEGORIES: readonly DraftErrorCategory[] = [
  'unreadable',
  'network',
  'auth',
  'quota',
  'invalid-mode',
  'rejected',
  'unknown',
  'cancelled',
]

export function isDraftErrorCategory(value: unknown): value is DraftErrorCategory {
  return typeof value === 'string' && (DRAFT_ERROR_CATEGORIES as readonly string[]).includes(value)
}
